import {
  GoogleAuthProvider,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getAuthClient, getFirestoreClient } from "./firebaseClient";
import {
  trackAuthAttempt,
  trackAuthFailure,
  trackAuthRedirectStarted,
  trackAuthSuccess,
  trackSignOut,
} from "./analyticsService";

let redirectResultPromise = null;
const REDIRECT_STORAGE_KEY = "barswiper-auth-redirect-pending";
const AUTH_SESSION_HINT_KEY = "barswiper-auth-session-hint";
const authFlowListeners = new Set();
let authFlowState = {
  active: false,
  title: "Loading",
  message: "Please wait while Barswiper finishes your request.",
};

const GOOGLE_REDIRECT_FALLBACK_CODES = new Set([
  "auth/popup-blocked",
  "auth/cancelled-popup-request",
  "auth/operation-not-supported-in-this-environment",
]);

function emitAuthFlowState(nextState) {
  authFlowState = {
    ...authFlowState,
    ...nextState,
  };
  authFlowListeners.forEach((listener) => listener(authFlowState));
}

function setRedirectPendingFlag(enabled) {
  if (typeof window === "undefined") return;

  if (enabled) {
    window.sessionStorage.setItem(REDIRECT_STORAGE_KEY, "1");
    return;
  }

  window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
}

function hasRedirectPendingFlag() {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(REDIRECT_STORAGE_KEY) === "1";
}

function setAuthSessionHint(enabled) {
  if (typeof window === "undefined") return;

  if (enabled) {
    window.localStorage.setItem(AUTH_SESSION_HINT_KEY, "1");
    return;
  }

  window.localStorage.removeItem(AUTH_SESSION_HINT_KEY);
}

export function hasKnownUserSessionHint() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(AUTH_SESSION_HINT_KEY) === "1";
}

async function persistAuth() {
  const auth = getAuthClient();
  await setPersistence(auth, browserLocalPersistence);
  return auth;
}

async function upsertUserProfile(user) {
  const db = getFirestoreClient();
  const providerIds = Array.isArray(user.providerData)
    ? user.providerData.map((item) => item?.providerId).filter(Boolean)
    : [];

  await setDoc(
    doc(db, "users", user.uid),
    {
      uid: user.uid,
      email: user.email ?? null,
      displayName: user.displayName ?? null,
      photoURL: user.photoURL ?? null,
      providerIds,
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
}

async function resolveRedirectResult() {
  if (redirectResultPromise) return redirectResultPromise;

  redirectResultPromise = (async () => {
    const shouldShowOverlay = hasRedirectPendingFlag();

    if (shouldShowOverlay) {
      emitAuthFlowState({
        active: true,
        title: "Finishing sign-in",
        message: "We are reconnecting your Google account and loading your Barswiper session.",
      });
    }

    try {
      const auth = await persistAuth();
      const result = await getRedirectResult(auth);
      if (result?.user) {
        setAuthSessionHint(true);
        await upsertUserProfile(result.user);
      }
      return result;
    } catch (error) {
      console.error("Failed to resolve Firebase redirect result:", error);
      return null;
    } finally {
      if (shouldShowOverlay) {
        setRedirectPendingFlag(false);
        emitAuthFlowState({ active: false });
      }
    }
  })();

  return redirectResultPromise;
}

export function getCurrentUser() {
  return getAuthClient().currentUser;
}

export function observeUserAuth(callback) {
  void resolveRedirectResult();
  return onAuthStateChanged(getAuthClient(), (user) => {
    setAuthSessionHint(Boolean(user));
    callback(user);
  });
}

export function observeAuthFlowState(callback) {
  authFlowListeners.add(callback);
  callback(authFlowState);

  return () => {
    authFlowListeners.delete(callback);
  };
}

export async function signInUserWithGoogle() {
  const auth = await persistAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account",
  });
  trackAuthAttempt("google", "signin");

  try {
    const result = await signInWithPopup(auth, provider);
    setAuthSessionHint(true);
    await upsertUserProfile(result.user);
    trackAuthSuccess("google", "signin");
    return { redirect: false, result };
  } catch (error) {
    if (!GOOGLE_REDIRECT_FALLBACK_CODES.has(error?.code)) {
      trackAuthFailure("google", "signin", error?.code);
      throw error;
    }

    setRedirectPendingFlag(true);
    trackAuthRedirectStarted("google");
    await signInWithRedirect(auth, provider);
    return { redirect: true };
  }
}

export async function signInUserWithEmail(email, password) {
  const auth = await persistAuth();
  trackAuthAttempt("email", "signin");
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    setAuthSessionHint(true);
    await upsertUserProfile(result.user);
    trackAuthSuccess("email", "signin");
    return result;
  } catch (error) {
    trackAuthFailure("email", "signin", error?.code);
    throw error;
  }
}

export async function signUpUserWithEmail({ email, password, displayName }) {
  const auth = await persistAuth();
  trackAuthAttempt("email", "signup");
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName) {
      await updateProfile(result.user, { displayName });
    }

    setAuthSessionHint(true);
    await upsertUserProfile({
      ...result.user,
      displayName: displayName || result.user.displayName,
    });

    trackAuthSuccess("email", "signup");
    return result;
  } catch (error) {
    trackAuthFailure("email", "signup", error?.code);
    throw error;
  }
}

export async function signOutUser() {
  trackSignOut();
  setAuthSessionHint(false);
  return signOut(getAuthClient());
}
